/**
 * GFG-273 (Campaigns tab, GFG Admin) — the table row's three-dot menu. Externally
 * anchored: the caller renders the three-dot button and owns `anchorEl`, so this component only
 * renders the floating item list and reports which action was picked.
 */
import { Menu, MenuItem, Tooltip } from "@mui/material";
import { useEffect, useRef } from "react";
import { COPY } from "@/constants/adminCampaigns";
import type { AdminCampaignRow } from "@/interfaces/adminCampaigns";

export interface CampaignRowMenuProps {
  campaign: AdminCampaignRow;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onImpersonate: () => void;
  onManageContacts: () => void;
  onManageGiftLinks: () => void;
  onPause: () => void;
  onResume: () => void;
  onCancel: () => void;
  onDisable: () => void;
  onEnable: () => void;
  onArchive: () => void;
  onRestore: () => void;
}

const ITEM_SX = {
  px: 1,
  py: "7px",
  cursor: "pointer",
  borderRadius: "6px",
  fontWeight: 500,
  fontSize: 14,
  lineHeight: "20px",
  color: "var(--color-graphite)",
  "&:hover": { bgcolor: "var(--color-light-dust)" },
} as const;

const ARCHIVE_ITEM_SX = {
  ...ITEM_SX,
  color: "var(--color-good-wine)",
} as const;

export default function CampaignRowMenu({
  campaign,
  anchorEl,
  onClose,
  onImpersonate,
  onManageContacts,
  onManageGiftLinks,
  onPause,
  onResume,
  onCancel,
  onDisable,
  onEnable,
  onArchive,
  onRestore,
}: CampaignRowMenuProps) {
  const isOpen = Boolean(anchorEl);
  const { allowedActions: allowed, impersonation } = campaign;

  // Table scroll and window resize both leave the menu anchored to a button that has moved (or
  // left the viewport); closing here is what stops it floating detached over the table. Capture
  // phase also catches scroll on the table's own scroll container, which never bubbles to window.
  useEffect(() => {
    if (!isOpen) return;
    window.addEventListener("scroll", onClose, true);
    window.addEventListener("resize", onClose);
    return () => {
      window.removeEventListener("scroll", onClose, true);
      window.removeEventListener("resize", onClose);
    };
  }, [isOpen, onClose]);

  // MUI restores focus to the three-dot trigger when the menu finishes its exit transition, a few
  // hundred ms after the click. Running the action now would open a dialog and then have its focus
  // yanked back to the table, so the action waits for the menu to actually be gone.
  const pendingAction = useRef<(() => void) | null>(null);

  function handleItemClick(action: () => void) {
    pendingAction.current = action;
    onClose();
  }

  function handleExited() {
    const action = pendingAction.current;
    pendingAction.current = null;
    action?.();
  }

  return (
    <Menu
      anchorEl={anchorEl}
      open={isOpen}
      onClose={onClose}
      MenuListProps={{ "aria-label": `Actions for ${campaign.name}` }}
      slotProps={{ transition: { onExited: handleExited } }}
      PaperProps={{
        sx: {
          minWidth: "184px",
          bgcolor: "white",
          border: "1px solid var(--color-smoke)",
          borderRadius: "8px",
          boxShadow: "0 6px 24px rgba(0,0,0,.14)",
          p: "6px",
        },
      }}
    >
      {/* The server decides which items a row offers (its `allowedActions`); only Impersonate and
          Archive can show disabled, each with the server's reason as the tooltip. */}
      <Tooltip title={impersonation.allowed ? "" : (impersonation.reason ?? "")} placement="left">
        <span>
          <MenuItem
            sx={ITEM_SX}
            disabled={!impersonation.allowed}
            onClick={() => handleItemClick(onImpersonate)}
          >
            {COPY.rowMenu.impersonate}
          </MenuItem>
        </span>
      </Tooltip>
      {allowed.manageContacts && (
        <MenuItem sx={ITEM_SX} onClick={() => handleItemClick(onManageContacts)}>
          {COPY.rowMenu.manageContacts}
        </MenuItem>
      )}
      {allowed.manageGiftLinks && (
        <MenuItem sx={ITEM_SX} onClick={() => handleItemClick(onManageGiftLinks)}>
          {COPY.rowMenu.manageGiftLinks}
        </MenuItem>
      )}
      {allowed.pause && (
        <MenuItem sx={ITEM_SX} onClick={() => handleItemClick(onPause)}>
          {COPY.rowMenu.pause}
        </MenuItem>
      )}
      {allowed.resume && (
        <MenuItem sx={ITEM_SX} onClick={() => handleItemClick(onResume)}>
          {COPY.rowMenu.resume}
        </MenuItem>
      )}
      {allowed.cancel && (
        <MenuItem sx={ITEM_SX} onClick={() => handleItemClick(onCancel)}>
          {COPY.rowMenu.cancel}
        </MenuItem>
      )}
      {allowed.disable && (
        <MenuItem sx={ITEM_SX} onClick={() => handleItemClick(onDisable)}>
          {COPY.rowMenu.disable}
        </MenuItem>
      )}
      {allowed.enable && (
        <MenuItem sx={ITEM_SX} onClick={() => handleItemClick(onEnable)}>
          {COPY.rowMenu.enable}
        </MenuItem>
      )}
      {allowed.archive.offered && (
        <Tooltip title={allowed.archive.reason ?? ""} placement="left">
          <span>
            <MenuItem
              sx={ARCHIVE_ITEM_SX}
              disabled={!allowed.archive.allowed}
              onClick={() => handleItemClick(onArchive)}
            >
              {COPY.rowMenu.archive}
            </MenuItem>
          </span>
        </Tooltip>
      )}
      {allowed.restore && (
        <MenuItem sx={ITEM_SX} onClick={() => handleItemClick(onRestore)}>
          {COPY.rowMenu.restore}
        </MenuItem>
      )}
    </Menu>
  );
}
